import { Dive } from '../types/Dive';
import { DiveSite, DiveSiteMatch } from '../types/DiveSite';
import { getDistanceKm } from './geoDistance';

const getTextTokens = (dive: Dive): string => {
  return [dive.diveName, dive.location, dive.description]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
};

const findCoordinateMatches = (dive: Dive, sites: DiveSite[]) => {
  if (dive.latitude == null || dive.longitude == null) {
    return [];
  }

  return sites
    .map((site) => ({
      site,
      distanceKm: getDistanceKm(dive.latitude!, dive.longitude!, site.latitude, site.longitude),
    }))
    .filter((candidate) => candidate.distanceKm <= candidate.site.matchRadiusKm)
    .sort((left, right) => {
      const leftScore = left.distanceKm / left.site.matchRadiusKm;
      const rightScore = right.distanceKm / right.site.matchRadiusKm;

      if (leftScore !== rightScore) {
        return leftScore - rightScore;
      }

      return left.distanceKm - right.distanceKm;
    });

};

const findTextMatches = (dive: Dive, sites: DiveSite[]) => {
  const tokens = getTextTokens(dive);
  if (!tokens) {
    return [];
  }

  return sites
    .flatMap((site) => {
      const phrases = [site.name, ...(site.aliases ?? [])].map((value) => value.toLowerCase());
      const matchedPhrase = phrases
        .filter((phrase) => tokens.includes(phrase))
        .sort((left, right) => right.length - left.length)[0];

      if (!matchedPhrase) {
        return [];
      }

      return [{ site, matchedPhrase }];
    })
    .sort((left, right) => right.matchedPhrase.length - left.matchedPhrase.length);

};

export const matchDiveToSite = (dive: Dive, sites: DiveSite[], ignoredSiteIds: string[] = []) => {
  const coordinateMatch = findCoordinateMatches(dive, sites).find(({ site }) => !ignoredSiteIds.includes(site.id));
  if (coordinateMatch) {
    return {
      site: coordinateMatch.site,
      matchType: 'coordinates' as const,
      distanceKm: coordinateMatch.distanceKm,
    };
  }

  const textMatch = findTextMatches(dive, sites).find(({ site }) => !ignoredSiteIds.includes(site.id));
  if (textMatch) {
    return {
      site: textMatch.site,
      matchType: 'text' as const,
      distanceKm: undefined,
    };
  }

  return null;
};

export const buildDiveSiteMatches = (
  dives: Dive[],
  sites: DiveSite[],
  ignoredDiveSiteMatches: Record<string, string[]> = {},
): Map<string, DiveSiteMatch> => {
  const matches = new Map<string, DiveSiteMatch>();

  dives.forEach((dive) => {
    const matched = matchDiveToSite(dive, sites, ignoredDiveSiteMatches[dive.id] ?? []);
    if (!matched) {
      return;
    }

    const existing = matches.get(matched.site.id);
    if (!existing) {
      matches.set(matched.site.id, {
        site: matched.site,
        matchedDives: [dive],
        diveCount: 1,
        lastVisited: dive.startTime,
        matchType: matched.matchType,
        nearestDistanceKm: matched.distanceKm,
      });
      return;
    }

    existing.matchedDives.push(dive);
    existing.diveCount += 1;

    if (new Date(dive.startTime) > new Date(existing.lastVisited)) {
      existing.lastVisited = dive.startTime;
    }

    if (matched.matchType === 'coordinates') {
      existing.matchType = 'coordinates';
      if (matched.distanceKm != null) {
        existing.nearestDistanceKm = existing.nearestDistanceKm == null
          ? matched.distanceKm
          : Math.min(existing.nearestDistanceKm, matched.distanceKm);
      }
    }
  });

  matches.forEach((match) => {
    match.matchedDives.sort((left, right) => {
      return new Date(right.startTime).getTime() - new Date(left.startTime).getTime();
    });
  });

  return matches;
};